import { createSignal, type Accessor, type Setter } from 'solid-js';
import type {
  CloudbeesFeatureFlagContext,
  CloudbeesOptions,
  FeatureFlagSource,
} from '../types';
import {
  getSourceFlagName,
  getSourceFlagNames,
  type FeatureFlag,
} from '../featureFlags';
import {
  isRoxFlagEnabled,
  onRoxConfigurationFetched,
  setRoxContext,
  setupRox,
} from './roxClient';

const toRoxContext = (
  context?: CloudbeesFeatureFlagContext
): Record<string, string | undefined> => ({
  /* eslint-disable @typescript-eslint/naming-convention -- names must match the custom properties used by the Cloudbees targeting rules */
  'User.UID': context?.userId,
  'User.Email': context?.email,
  'Account.UID': context?.accountId,
  'Account.Name': context?.accountName,
  /* eslint-enable @typescript-eslint/naming-convention */
});

const CLOUDBEES_FLAG_LABELS = getSourceFlagNames('cloudbees');

const haveSameLabels = (
  previous: ReadonlySet<string>,
  next: ReadonlySet<string>
): boolean =>
  previous.size === next.size &&
  [...next].every((label) => previous.has(label));

/**
 * Feature flag source backed by CloudBees Feature Management (`rox-browser`).
 *
 * Only answers for flags that have a Cloudbees name, looked up by that label.
 * Flags are disabled until `initialize` has resolved, then re-evaluated whenever
 * the context changes or Rox fetches a changed configuration. Rox is a page-wide
 * singleton, so it is set up once however many instances exist; call `dispose`
 * to stop listening for configuration updates.
 */
export class CloudbeesFeatureFlagProvider implements FeatureFlagSource {
  public readonly name = 'cloudbees';
  private readonly enabledLabels: Accessor<ReadonlySet<string>>;
  private readonly setEnabledLabels: Setter<ReadonlySet<string>>;
  private stopListeningToRox: (() => void) | undefined;
  private isInitialized = false;
  private isDisposed = false;
  private context: CloudbeesFeatureFlagContext | undefined;

  constructor(private readonly options?: CloudbeesOptions) {
    const [enabledLabels, setEnabledLabels] = createSignal<ReadonlySet<string>>(
      new Set(),
      { equals: haveSameLabels }
    );
    this.enabledLabels = enabledLabels;
    this.setEnabledLabels = setEnabledLabels;
  }

  public async initialize(): Promise<void> {
    if (!this.options?.apiKey) {
      throw new Error('A Cloudbees API key is required');
    }
    if (this.isDisposed) {
      return;
    }
    setRoxContext(toRoxContext(this.context));
    await setupRox(this.options.apiKey);
    // Dispose can run during this await, before the listener exists. Attaching
    // it afterward would leave the page-wide Rox singleton calling evaluate.
    if (this.isDisposed) {
      return;
    }
    // setContext during setup only stores the value until isInitialized.
    // Push that latest context before the first evaluate, or targeting stays
    // on the pre-await snapshot.
    setRoxContext(toRoxContext(this.context));
    this.isInitialized = true;
    this.stopListeningToRox = onRoxConfigurationFetched(() => this.evaluate());
    this.evaluate();
  }

  public setContext(context: CloudbeesFeatureFlagContext): void {
    this.context = context;
    if (this.isDisposed || !this.isInitialized) {
      return;
    }
    setRoxContext(toRoxContext(context));
    this.evaluate();
  }

  public hasFlag(flag: FeatureFlag): boolean {
    const label = getSourceFlagName(flag, 'cloudbees');
    return label !== undefined && this.enabledLabels().has(label);
  }

  public dispose(): void {
    this.isDisposed = true;
    this.stopListeningToRox?.();
    this.stopListeningToRox = undefined;
  }

  private evaluate(): void {
    this.setEnabledLabels(
      new Set(CLOUDBEES_FLAG_LABELS.filter(isRoxFlagEnabled))
    );
  }
}
